/**
 * StreetcarRig — NAV-02 scripted ride from Birmingham → Lone Pine Gap cut →
 * Edgewood Lake north shore.
 *
 * HISTORY:
 *   Plan 05-01 Task 2 — original first-person rig positioning the camera
 *     AT `curve.getPointAt(t)`.
 *   Plan 06-05 Task 3 — added a visible streetcar body (no39.glb) that
 *     mirrored the camera pose with a vertical offset; camera still sat
 *     INSIDE the car.
 *   Plan 08 polish — added a camera-parented StreetcarInterior .glb (window
 *     frame + seat back) so the first-person view read as "inside a
 *     streetcar looking out a window."
 *   Plan 08 chase-cam pass (2026-04-24) — flipped the POV. The camera now
 *     sits BEHIND and slightly ABOVE the streetcar. The No. 39 body is the
 *     hero of the frame, not an invisible container. First-person interior
 *     is no longer mounted; interior.glb stays on disk unused for now.
 *
 * WHY THE CHASE CAM:
 *   Live-site Playwright ride capture on 2026-04-24 showed the interior
 *   frame consuming the lower 60% of the screen — the "looking through a
 *   window" read never landed because the authored interior geometry was a
 *   large dark mass relative to the camera's framing. A third-person chase
 *   camera gives the user:
 *     1. Unobstructed view of the scene (Lone Pine Gap, lake, pavilion)
 *     2. A visible, moving streetcar they can anchor to
 *     3. A much clearer sense of speed + position along the route
 *   This pass addresses the NPR aesthetic brief (Rule 6 — stylized
 *   silhouettes) better: the streetcar IS a stylized silhouette, so
 *   watching it travel through the scene is the intended cinematographic
 *   read for the experience.
 *
 * CONTRACT:
 *   - Builds the curve from `manifest.streetcarSpline.waypoints` via
 *     `buildStreetcarCurve` (useMemo keyed on the waypoints array).
 *   - Rides at arc-length-uniform speed via `curve.getPointAt(t)` — NOT
 *     `getPoint(t)` (Pitfall 1 closure).
 *   - Streetcar body sits AT `curve.getPointAt(t)` every frame.
 *   - Camera sits BEHIND + ABOVE the body along the forward tangent.
 *   - Camera looks at `curve.getPointAt(t + LOOK_AHEAD_FRAC)` — a point
 *     slightly ahead of the body so the body frames in the lower half of
 *     the screen with road/rail ahead visible above it.
 *   - Subtle Z-axis sway (±0.008 rad at 1.3Hz) kept so the ride still
 *     reads as a moving camera rig, not a perfect dolly.
 *   - Writes pose to `useNavStore.setCameraPose` every frame (Minimap /
 *     DebugHUD read this).
 *   - Writes progress to `useStreetcarStore.setProgress` every frame.
 *   - Fires END_OF_LINE via `useNavSend` exactly once when elapsed >=
 *     durationS (send-once guard ref).
 *
 * BOARDING PAUSE:
 *   When `paused` prop is true (NavRig mounts `<StreetcarRig paused />`
 *   during the `boarding` state), the rig freezes elapsed at 0 so the
 *   camera sits behind the parked car at the platform while the machine's
 *   `after: 1500` delayed transition runs.
 *
 * AudioListener NOT mounted here — Plan 05-05 owns that via
 * AudioListenerBinding; decoupling keeps this rig focused on motion.
 */
import { useFrame, useThree } from '@react-three/fiber';
import { useEffect, useMemo, useRef } from 'react';
import * as THREE from 'three';
import { useManifest } from '../content/useManifest';
import { useNavSend } from './NavContext';
import { useNavStore } from '../store/useNavStore';
import { useStreetcarStore } from '../store/useStreetcarStore';
import { buildStreetcarCurve, DEFAULT_RIDE_DURATION_S } from './streetcarSpline';
import { StreetcarBody, applyStreetcarBodyPose } from './StreetcarBody';

export const SWAY_AMPLITUDE_RAD = 0.008; // <= 0.01 rad per plan spec
export const SWAY_FREQ_HZ = 1.3;

/**
 * Chase-cam offsets (world units, metres).
 *   CHASE_BACK — distance camera sits behind the streetcar along the
 *     forward tangent. Body is 12m long; 14m puts the camera clear of the
 *     rear cab with room to frame the full car plus a hair of road behind.
 *   CHASE_UP — world-space vertical lift of the camera above the spline
 *     point. Body roofline sits ~1.4m above the spline point
 *     (BODY_FLOOR_LIFT offset in StreetcarBody). 5m of camera lift gets
 *     the eye roughly 3.6m above the roofline — a pleasant elevated
 *     chase angle looking slightly DOWN at the car.
 *   LOOK_AHEAD_FRAC — fraction of total curve length to peek ahead of the
 *     current spline point when computing the camera's lookAt target.
 *     Must be LARGER than the body's own look-ahead so the camera frames
 *     the body + road ahead, not just the body. 0.03 at durationS=90s
 *     means the camera looks roughly 2.7s ahead of the streetcar's
 *     current position.
 *   BODY_LOOK_AHEAD_FRAC — tiny look-ahead used for the body's own
 *     orientation (so the body always points along its direction of
 *     motion). Separate from LOOK_AHEAD_FRAC because the body turns more
 *     tightly than the camera's framing peek.
 */
export const CHASE_BACK = 14;
export const CHASE_UP = 5;
export const LOOK_AHEAD_FRAC = 0.03;
export const BODY_LOOK_AHEAD_FRAC = 0.01;

export function StreetcarRig({ paused = false }: { paused?: boolean }) {
  const { camera } = useThree();
  const send = useNavSend();
  const { manifest } = useManifest();

  // Build the curve once per manifest load (useMemo keyed on waypoints
  // reference). If the manifest changes at runtime (hot reload), this rebuilds.
  const curve = useMemo(() => {
    if (!manifest?.streetcarSpline) return null;
    return buildStreetcarCurve(manifest.streetcarSpline.waypoints);
  }, [manifest?.streetcarSpline]);

  const durationS =
    manifest?.streetcarSpline?.durationS ?? DEFAULT_RIDE_DURATION_S;

  // Accumulated elapsed time — uses R3F's delta (seconds) instead of
  // performance.now() so tests can drive it via advanceFrames() with a
  // controlled clock.
  const elapsedRef = useRef(0);
  // Send-once guard prevents double END_OF_LINE fires when useFrame ticks
  // continue to run after t=1.
  const endOfLineFiredRef = useRef(false);

  // Reset guards whenever the curve reference changes (re-mount = new ride).
  useEffect(() => {
    elapsedRef.current = 0;
    endOfLineFiredRef.current = false;
  }, [curve]);

  // Reusable scratch vectors — avoid per-frame allocation.
  const bodyPosScratch = useRef(new THREE.Vector3());
  const bodyAheadScratch = useRef(new THREE.Vector3());
  const cameraLookScratch = useRef(new THREE.Vector3());
  const forwardScratch = useRef(new THREE.Vector3());
  const cameraPosScratch = useRef(new THREE.Vector3());

  // Streetcar body group ref — driven every frame.
  const bodyRef = useRef<THREE.Group | null>(null);

  useFrame((_state, delta) => {
    if (!curve) return; // manifest not loaded yet

    if (!paused) {
      elapsedRef.current += delta;
    }
    const elapsed = elapsedRef.current;
    const t = Math.min(elapsed / durationS, 1);

    // 1) Streetcar body pose — at spline(t), oriented toward spline(t + eps).
    bodyPosScratch.current.copy(curve.getPointAt(t));
    const bodyLookT = Math.min(t + BODY_LOOK_AHEAD_FRAC, 1);
    bodyAheadScratch.current.copy(curve.getPointAt(bodyLookT));
    applyStreetcarBodyPose(
      bodyRef.current,
      bodyPosScratch.current,
      bodyAheadScratch.current,
    );

    // 2) Camera pose — behind + above the body, looking ahead of it.
    // Forward tangent derived from (bodyAhead - bodyPos) normalized.
    forwardScratch.current
      .copy(bodyAheadScratch.current)
      .sub(bodyPosScratch.current);
    // Guard: if the look-ahead landed on the endpoint (t=1), tangent has
    // zero length. Fall back to the camera's previous forward so the
    // camera doesn't snap to origin.
    if (forwardScratch.current.lengthSq() < 1e-8) {
      // Reuse a tiny step back from the previous segment instead of
      // current tangent. Cheap: derivative approximation.
      const prevT = Math.max(t - 0.001, 0);
      forwardScratch.current
        .copy(bodyPosScratch.current)
        .sub(curve.getPointAt(prevT));
    }
    forwardScratch.current.normalize();

    // cameraPos = bodyPos - CHASE_BACK * forward + CHASE_UP * worldUp
    cameraPosScratch.current
      .copy(bodyPosScratch.current)
      .addScaledVector(forwardScratch.current, -CHASE_BACK);
    cameraPosScratch.current.y += CHASE_UP;
    camera.position.copy(cameraPosScratch.current);

    // 3) Camera lookAt — a point further ahead of the body so the body
    // is framed in the lower center of the screen with road/rail ahead.
    const cameraLookT = Math.min(t + LOOK_AHEAD_FRAC, 1);
    cameraLookScratch.current.copy(curve.getPointAt(cameraLookT));
    camera.lookAt(cameraLookScratch.current);

    // 4) Subtle sway applied AFTER lookAt so it reads as ride motion. Only
    // when moving (not during the boarding platform view).
    if (!paused) {
      camera.rotation.z += Math.sin(elapsed * SWAY_FREQ_HZ) * SWAY_AMPLITUDE_RAD;
    }

    // 5) Persist pose + progress.
    useNavStore.getState().setCameraPose(
      [camera.position.x, camera.position.y, camera.position.z],
      [
        camera.quaternion.x,
        camera.quaternion.y,
        camera.quaternion.z,
        camera.quaternion.w,
      ],
    );
    useStreetcarStore.getState().setProgress(t);

    // 6) END_OF_LINE at t=1 — send once, via useNavSend.
    if (t >= 1 && !endOfLineFiredRef.current && !paused) {
      endOfLineFiredRef.current = true;
      send({ type: 'END_OF_LINE' });
    }
  });

  // Render the streetcar body as a scene child. The body is driven every
  // frame via bodyRef in useFrame above.
  return <StreetcarBody bodyRef={bodyRef} />;
}
